import { WorkflowHiringReadinessExplanationWriteBackDetails } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-explanation-write-back/workflow-hiring-readiness-explanation-write-back';
import { WorkflowHiringReadinessExplanationWriteBackV2Config } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-explanation-write-back/workflow-hiring-readiness-explanation-write-back';
import React, { useCallback } from 'react';
import { ConfigureHiringReadinessExplanationWriteBackStrategy } from 'apps/sigma2-ui/src/components/workflows/ConfigureHiringReadinessExplanationWriteBackStrategy';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { ConfigureWorkflowBaseProps } from 'apps/sigma2-ui/src/components/workflows/types';
import { WorkflowWriteBackMode } from 'libs/end-to-end/src/types/databases/workflows/workflow-write-back-mode';
import { ConfigureWriteBackMode } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackMode';
import { ConfigureHiringReadinessExplanationWriteBackV2Config } from 'apps/sigma2-ui/src/components/workflows/ConfigureHiringReadinessExplanationWriteBackV2Config';
import { WorkflowExplanationFormat } from 'libs/end-to-end/src/types/databases/workflows/workflow-explanation-format';
import { WorkflowSimplifiedExplanationConfig } from 'libs/end-to-end/src/types/databases/workflows/workflow-explanation-format';
import { ConfigureSimplifiedExplanationConfig } from 'apps/sigma2-ui/src/components/workflows/ConfigureSimplifiedExplanationConfig';

export interface ConfigureHiringReadinessExplanationWriteBackDetailsProps
  extends ConfigureWorkflowBaseProps {
  details: WorkflowHiringReadinessExplanationWriteBackDetails | null;
  updateDetails: (
    details: WorkflowHiringReadinessExplanationWriteBackDetails | null,
  ) => void;
}

export const ConfigureHiringReadinessExplanationWriteBackDetails = ({
  details,
  updateDetails,
  listSecretsPattern,
  organizationId,
  listDataSetsPattern,
}: ConfigureHiringReadinessExplanationWriteBackDetailsProps) => {
  const updateDetailsPartial = useCallback(
    (partial: Partial<WorkflowHiringReadinessExplanationWriteBackDetails>) => {
      updateDetails({
        type: 'hiringReadinessExplanationWriteBack',
        format: 'long',
        ...details,
        ...partial,
      });
    },
    [details, updateDetails],
  );

  const updateStrategy = useCallback(
    (
      strategy:
        | null
        | WorkflowHiringReadinessExplanationWriteBackDetails['strategy'],
    ) => {
      updateDetailsPartial({
        strategy: strategy ?? undefined,
      });
    },
    [updateDetailsPartial],
  );

  const toggleWriteBackEnabled = useCallback(() => {
    updateDetails(
      details
        ? null
        : {
            type: 'hiringReadinessExplanationWriteBack',
            format: 'long',
          },
    );
  }, [details, updateDetails]);

  const updateMode = useCallback(
    (mode: WorkflowWriteBackMode) => {
      updateDetailsPartial({
        mode,
      });
    },
    [updateDetailsPartial],
  );

  const updateSkipWriteBackIfDuplicate = useCallback(
    (skipWriteBackIfDuplicate: boolean) => {
      updateDetailsPartial({ skipWriteBackIfDuplicate });
    },
    [updateDetailsPartial],
  );

  const updatePreviousDaysOfApplicants = useCallback(
    (numPreviousDaysOfApplicants: number | undefined) => {
      updateDetailsPartial({
        numPreviousDaysOfApplicants:
          numPreviousDaysOfApplicants !== undefined &&
          isNaN(numPreviousDaysOfApplicants)
            ? undefined
            : numPreviousDaysOfApplicants,
      });
    },
    [updateDetailsPartial],
  );

  const updateMinReadinessRating = useCallback(
    (minReadinessRating: number | undefined) => {
      updateDetailsPartial({
        minReadinessRating:
          minReadinessRating !== undefined && isNaN(minReadinessRating)
            ? undefined
            : minReadinessRating,
      });
    },
    [updateDetailsPartial],
  );

  const updateNumberOfSamplesToSend = useCallback(
    (numberOfSamplesToSend: number | undefined) => {
      updateDetailsPartial({
        numberOfSamplesToSend:
          numberOfSamplesToSend !== undefined && isNaN(numberOfSamplesToSend)
            ? undefined
            : numberOfSamplesToSend,
      });
    },
    [updateDetailsPartial],
  );

  const updateFormat = useCallback(
    (format: WorkflowExplanationFormat) => {
      updateDetailsPartial({ format });
    },
    [updateDetailsPartial],
  );

  const updateSimplifiedExplanationConfig = useCallback(
    (
      simplifiedExplanationConfig: WorkflowSimplifiedExplanationConfig | null,
    ) => {
      updateDetailsPartial({
        simplifiedExplanationConfig: simplifiedExplanationConfig ?? undefined,
      });
    },
    [updateDetailsPartial],
  );

  const updateV2Config = useCallback(
    (v2Config: WorkflowHiringReadinessExplanationWriteBackV2Config | null) => {
      updateDetailsPartial({
        v2Config: v2Config ?? undefined,
      });
    },
    [updateDetailsPartial],
  );

  const updateLimitExplanationFactors = useCallback(
    (limitExplanationFactors: boolean) => {
      updateDetailsPartial({ limitExplanationFactors });
    },
    [updateDetailsPartial],
  );

  return (
    <div className="space-y-6 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold">
        Configure Hiring Readiness Explanation Write Back
      </h2>
      <S2Checkbox checked={Boolean(details)} onChange={toggleWriteBackEnabled}>
        Enable the hiring readiness explanation write back
      </S2Checkbox>
      {details && (
        <>
          <ConfigureHiringReadinessExplanationWriteBackStrategy
            strategy={details.strategy ?? null}
            updateStrategy={updateStrategy}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
            listDataSetsPattern={listDataSetsPattern}
          />

          <ConfigureWriteBackMode
            mode={details.mode ?? null}
            updateMode={updateMode}
            skipWriteBackIfDuplicate={details.skipWriteBackIfDuplicate ?? false}
            updateSkipWriteBackIfDuplicate={updateSkipWriteBackIfDuplicate}
          />

          <label className="space-y-1 flex flex-col">
            <span>Number of previous days of applicants</span>

            <input
              type="text"
              value={details.numPreviousDaysOfApplicants ?? ''}
              onChange={(e) => {
                const numericValue = parseInt(e.target.value, 10);
                updatePreviousDaysOfApplicants(numericValue);
              }}
              className="form-input border rounded p-2"
            />
          </label>

          <label className="space-y-1 flex flex-col">
            <span>Format</span>
            <select
              className="form-select"
              value={details.format ?? 'long'}
              onChange={(e) => {
                updateFormat(e.target.value as WorkflowExplanationFormat);
              }}
            >
              <option value="long">Long</option>
              <option value="short">Short</option>
              <option value="simplified">Simplified</option>
            </select>
          </label>

          {details.format === 'simplified' && (
            <ConfigureSimplifiedExplanationConfig
              simplifiedExplanationConfig={
                details.simplifiedExplanationConfig ?? {}
              }
              onUpdateSimplifiedExplanationConfig={
                updateSimplifiedExplanationConfig
              }
            />
          )}

          <div className="space-y-1">
            <S2Checkbox
              checked={details.limitExplanationFactors ?? false}
              onChange={() => {
                updateLimitExplanationFactors(
                  !(details.limitExplanationFactors ?? false),
                );
              }}
            >
              Limit explanation to top 10 factors
            </S2Checkbox>
            <p className="text-xs text-gray-600 pl-6">
              Caps the number of traits included in the generated explanation
              text at 10. Applies to the long and short formats; the simplified
              format has its own per-section limits. Currently only supported
              for Fountain write-backs.
            </p>
          </div>

          <label className="space-y-1 flex flex-col">
            <span>Minimum readiness rating</span>

            <input
              type="text"
              value={details.minReadinessRating ?? ''}
              onChange={(e) => {
                const numericValue = parseInt(e.target.value, 10);
                updateMinReadinessRating(numericValue);
              }}
              className="form-input border rounded p-2"
            />
          </label>
          <label className="space-y-1 flex flex-col">
            <span>Number of Samples To Send</span>

            <input
              type="number"
              value={details.numberOfSamplesToSend ?? ''}
              onChange={(e) => {
                updateNumberOfSamplesToSend(e.target.valueAsNumber);
              }}
              className="form-input border rounded p-2"
            />
          </label>

          <ConfigureHiringReadinessExplanationWriteBackV2Config
            v2Config={details.v2Config ?? null}
            updateV2Config={updateV2Config}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
            listDataSetsPattern={listDataSetsPattern}
            legacyStrategy={details.strategy}
            legacyMode={details.mode}
            legacyFormat={details.format}
            legacySimplifiedExplanationConfig={
              details.simplifiedExplanationConfig
            }
          />
        </>
      )}
    </div>
  );
};
